import {Observable, Subscription} from 'rxjs';

import ErrorType                   from '../../../Shared/Constants/ErrorType';
import logger                      from '../../../Shared/Util/logger';
import BufferingType               from '../../../State/Stores/Playback/Constants/BufferingType';
import PlaybackStatus              from '../../../State/Stores/Playback/Constants/PlaybackStatus';
import Playback                    from '../../../State/Stores/Playback/Playback';
import IAnalytics                  from '../../Shared/Interfaces/IAnalytics';
import IPlayerStateManagerResolver from '../Interfaces/IPlayerStateManagerResolver';
import ISessionCallback            from '../Interfaces/ISessionCallback';
import Conviva                     from '../Vendor/Conviva';

const handleLoading = (startSessionCallback: ISessionCallback) => startSessionCallback();

const handleBuffering = (getPlayerStateManager: IPlayerStateManagerResolver): void => {
    const {BUFFERING} = Conviva.PlayerStateManager.PlayerState;

    getPlayerStateManager().setPlayerState(BUFFERING);

    logger.log(`[Conviva] Setting player state to ${BUFFERING}`);
};

const handlePlaying = (
    getPlayerStateManager: IPlayerStateManagerResolver,
    updateSessionCallback: ISessionCallback
): void => {
    const {PLAYING} = Conviva.PlayerStateManager.PlayerState;

    getPlayerStateManager().setPlayerState(PLAYING);

    logger.log(`[Conviva] Setting player state to ${PLAYING}`);

    updateSessionCallback();
};

const handlePaused = (getPlayerStateManager: IPlayerStateManagerResolver): void => {
    const {PAUSED} = Conviva.PlayerStateManager.PlayerState;

    getPlayerStateManager().setPlayerState(PAUSED);

    logger.log(`[Conviva] Setting player state to ${PAUSED}`);
};

const handleEnded = (
    getPlayerStateManager: IPlayerStateManagerResolver,
    endSessionCallback: ISessionCallback
): void => {
    const {STOPPED} = Conviva.PlayerStateManager.PlayerState;

    getPlayerStateManager().setPlayerState(STOPPED);

    logger.log(`[Conviva] Setting player state to ${STOPPED}`);

    endSessionCallback();
};

const handleSeekStart = (
    getPlayerStateManager: IPlayerStateManagerResolver,
    seekPosition: number
): void => {
    getPlayerStateManager().setPlayerSeekStart(Math.round(seekPosition));

    logger.log(`[Conviva] Starting seek at ${seekPosition}`);
};

const handleSeekEnd = (
    getPlayerStateManager: IPlayerStateManagerResolver
): void => {
    getPlayerStateManager().setPlayerSeekEnd();

    logger.log(`[Conviva] Ending seek`);
};

const handleErrored = (
   client: Conviva.Client,
   sessionKeyResolver: () => number,
   fatalErrorType: ErrorType
): void => {
    client.reportError(
        sessionKeyResolver(),
        fatalErrorType,
        Conviva.Client.ErrorSeverity.FATAL
    );

    logger.log(`[Conviva] Reporting fatal error ${fatalErrorType}`);
};

const subscribeToPlaybackStatus = (
    playbackStatusWithPlayback$: Observable<[PlaybackStatus, Playback]>,
    client: Conviva.Client,
    getPlayerStateManager: IPlayerStateManagerResolver,
    analytics: IAnalytics
): Subscription => {
    let isSeeking = false;

    return playbackStatusWithPlayback$
        .subscribe(([playbackStatus, {
            bufferingType,
            currentTime,
            fatalErrorType
        }]) => {
            switch (playbackStatus) {
                case PlaybackStatus.UNLOADED:
                    analytics.endSession();

                    break;
                case PlaybackStatus.BUFFERING:
                    if (bufferingType === BufferingType.SEEK) {
                        isSeeking = true;

                        handleSeekStart(getPlayerStateManager, currentTime);
                    }

                    handleBuffering(getPlayerStateManager);

                    break;
                case PlaybackStatus.ENDED:
                    handleEnded(getPlayerStateManager, analytics.endSession.bind(analytics));

                    break;
                case PlaybackStatus.A_LOADING:
                case PlaybackStatus.B_LOADING:
                    handleLoading(analytics.startSession.bind(analytics));

                    break;
                case PlaybackStatus.PAUSED:
                    handlePaused(getPlayerStateManager);

                    break;
                case PlaybackStatus.PLAYING:
                    if (isSeeking) {
                        handleSeekEnd(getPlayerStateManager);

                        isSeeking = false;
                    }

                    handlePlaying(getPlayerStateManager, analytics.updateSession.bind(analytics));

                    break;
                case PlaybackStatus.ERRORED:
                    handleErrored(client, analytics.resolveSessionKey.bind(analytics), fatalErrorType);

                    break;
            }
        });
};

export {
    subscribeToPlaybackStatus as default,
    handleBuffering,
    handleEnded,
    handleErrored,
    handleLoading,
    handlePaused,
    handlePlaying,
    handleSeekEnd,
    handleSeekStart
};